/**
 * AttendanceSettings.jsx — app Settings > Attendance Hours.
 * Full-day / short-leave / half-day hour thresholds + under-half-day status.
 * Moved out of People & HR (30-Sep-2026 Chunk C ruling) — IT department or
 * super_admin only. Every save is a NEW dated version (effective today or a
 * future date, never retroactive) and is audit-logged server-side.
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Clock, Save, Loader2, History } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { formatDate } from '../../lib/format';

export default function AttendanceSettings() {
  const { user: currentUser } = useAuth() || {};
  const canEdit = (() => {
    const role = (currentUser?.role || '').toLowerCase();
    const dept = (currentUser?.department || '').toUpperCase();
    return role === 'super_admin' || dept === 'IT';
  })();

  const todayStr = new Date().toISOString().slice(0, 10);
  const [policy, setPolicy] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [versions, setVersions] = useState([]);
  const [effectiveFrom, setEffectiveFrom] = useState(todayStr);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/hr/attendance/hours-policy')
      .then(d => { setPolicy(d.policy); setDefaults(d.defaults); setVersions(d.versions || []); })
      .catch(() => toast.error('Failed to load attendance hours policy'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!(policy.half_day_hours > 0 && policy.half_day_hours < policy.short_leave_hours && policy.short_leave_hours < policy.full_day_hours)) {
      toast.error('Thresholds must satisfy 0 < Half day < Short leave < Full day');
      return;
    }
    if (effectiveFrom < todayStr) {
      toast.error('Effective-from must be today or a future date — a change never recomputes past days');
      return;
    }
    setSaving(true);
    try {
      await apiPut('/hr/attendance/hours-policy', { ...policy, effective_from: effectiveFrom });
      toast.success(`Attendance hour thresholds updated, effective ${effectiveFrom}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading || !policy) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;

  const field = (key, label, hint) => (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      <input
        type="number" step="0.5" min="0" value={policy[key]} disabled={!canEdit}
        onChange={(e) => setPolicy({ ...policy, [key]: parseFloat(e.target.value) || 0 })}
        data-testid={`attendance-hours-${key}`}
        className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm disabled:bg-slate-100"
      />
      <p className="text-[10px] text-slate-400 mt-1">{hint} · default {defaults[key]}</p>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="attendance-settings-page">
      <PageHeader
        overline="Settings · Attendance"
        title="Attendance Hours"
        subtitle="Hour thresholds that decide a day's status (Present / Half Day / Absent) and the monthly short-leave quota. Feeds Auto-Mark, the mobile punch, Missed Punch approval and payroll. IT / Super Admin only."
      />
      {!canEdit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid="attendance-settings-readonly-banner">
          Read-only — only IT department or Super Admin can change these thresholds.
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={14} className="text-[#3b4872]" />
          <div className="kpi-overline">Day-status thresholds (hours worked)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {field('full_day_hours', 'Full day (Present)', 'Hours worked at/above this = full Present')}
          {field('short_leave_hours', 'Short leave', 'Below full day but at/above this = Present, using 1 of 2 monthly short leaves')}
          {field('half_day_hours', 'Half day', 'Below short-leave but at/above this = Half Day')}
        </div>
        <div className="mt-5 pt-5 border-t border-slate-100">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Under half-day status</label>
          <select
            value={policy.under_half_day_status} disabled={!canEdit}
            onChange={(e) => setPolicy({ ...policy, under_half_day_status: e.target.value })}
            data-testid="attendance-hours-under_half_day_status"
            className="w-full md:w-64 px-3 py-2.5 border border-slate-300 rounded text-sm bg-white disabled:bg-slate-100"
          >
            <option value="A">Absent</option>
            <option value="H">Half day</option>
          </select>
          <p className="text-[10px] text-slate-400 mt-1">
            Status for days below the Half day threshold with SOME hours worked · default Absent.
            No punch-in at all is always Absent, regardless of this setting.
          </p>
        </div>
        {canEdit && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Effective from</label>
            <input
              type="date" min={todayStr} value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              data-testid="attendance-hours-effective-from"
              className="w-full md:w-64 px-3 py-2.5 border border-slate-300 rounded text-sm"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Today or a future date only — a change never recomputes days already passed or a month already in a payroll run.
            </p>
          </div>
        )}
        <div className="mt-5 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600 leading-relaxed" data-testid="attendance-hours-preview">
          <b>{policy.full_day_hours}h+</b> → Present &nbsp;·&nbsp;
          <b>{policy.short_leave_hours}h–{policy.full_day_hours}h</b> → Present (short leave, 2/month) else Half Day &nbsp;·&nbsp;
          <b>{policy.half_day_hours}h–{policy.short_leave_hours}h</b> → Half Day &nbsp;·&nbsp;
          <b>&lt;{policy.half_day_hours}h</b> → {policy.under_half_day_status === 'H' ? 'Half day' : 'Absent'} &nbsp;·&nbsp;
          <b>No punch-in</b> → Absent (always)
        </div>
        {canEdit && (
          <div className="mt-5 flex justify-end">
            <button
              onClick={save} disabled={saving} data-testid="attendance-hours-save-btn"
              className="px-5 py-2.5 text-sm bg-[#3b4872] text-white rounded font-display font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
      {versions.length > 0 && (
        <div className="mt-5 bg-white border border-slate-200 rounded-md p-5" data-testid="attendance-hours-version-history">
          <div className="flex items-center gap-2 mb-3">
            <History size={14} className="text-[#3b4872]" />
            <div className="kpi-overline">Version history</div>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 uppercase text-[10px]">
                <th className="pb-2">Effective from</th><th className="pb-2">Full day</th><th className="pb-2">Short leave</th><th className="pb-2">Half day</th><th className="pb-2">Under half-day</th><th className="pb-2">Changed by</th>
              </tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.effective_from} className="border-t border-slate-100" data-testid={`attendance-hours-version-${v.effective_from}`}>
                  <td className="py-1.5 font-semibold">{formatDate(v.effective_from)}</td>
                  <td className="py-1.5">{v.full_day_hours}h</td>
                  <td className="py-1.5">{v.short_leave_hours}h</td>
                  <td className="py-1.5">{v.half_day_hours}h</td>
                  <td className="py-1.5">{v.under_half_day_status === 'H' ? 'Half day' : 'Absent'}</td>
                  <td className="py-1.5 text-slate-500">{v.created_by || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
